function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

/* eslint-disable no-nested-ternary, no-use-before-define */
const _ = require('lodash');

function _call(body, then, direct) {
  if (direct) {
    return then ? then(body()) : body();
  }

  try {
    var result = Promise.resolve(body());
    return then ? result.then(then) : result;
  } catch (e) {
    return Promise.reject(e);
  }
}

const AccessContext = require('./contexts/access-context');

const PagedList = require('../entities/paged-list');

const {
  STAR_UUID
} = require('../utils/constants');

const {
  strList
} = require('../utils/serialize');

const promoteFields = ['id', 'roleId', 'userId', 'propertyId', 'businessId', 'locationId'];
const fields = ['id', 'role.id', 'role.sid', 'role.name', 'role.type', 'propertyId', 'businessId', 'locationId'];
const searchFields = ['user(id,email)', 'roleId', 'businessId', 'locationId'];
const businessSearchFields = ['id', 'name', 'shortName', 'slogan', 'desc', 'isEnabled', 'images', 'phone', 'url', 'categories(id,name)'];
const locationSearchFields = ['id', 'name', 'slug', 'url', 'phone', 'address(streetAddress,street,city,state,country,centre,suite)'];
const selfSearchFields = ['propertyId,businessId,locationId', `business(${businessSearchFields.join(',')})`, `location(${locationSearchFields.join(',')})`];

class AccessRepo {
  constructor(client
  /*: ApiClient*/
  , context
  /*: AccessContext*/
  = new AccessContext(null)) {
    this.client = client;
    this.context = context;
    this.permCache = new PermissionCache();
  }

  listMine(context
  /*: AccessContext*/
  ) {
    const {
      propertyId,
      businessId,
      locationId
    } = context && context.serialize() || {};

    const params = _.omitBy({
      propertyId,
      businessId,
      locationId
    }, val => val == null);

    const path = `/self/accesses`;
    const req = new this.client.Request(path, params, fields);
    req.useCursor = true;
    return this.client.get(req);
  }

  searchMine(context
  /*: AccessContext*/
  , requestFields
  /*: ?[string]*/
  , params
  /*: ?Object*/
  ) {
    // Pass ids as "*" to send "all access" UUID
    const {
      propertyId,
      businessId,
      locationId
    } = context && context.serialize() || {};
    const contextParams = {
      propertyId: (propertyId === '*' ? STAR_UUID : propertyId) || undefined,
      businessId: (businessId === '*' ? STAR_UUID : businessId) || undefined,
      locationId: (locationId === '*' ? STAR_UUID : locationId) || undefined
    };
    const requestParams = Object.assign(contextParams, params);
    const path = `/self/accesses/search`;
    const req = new this.client.Request(path, requestParams, requestFields || selfSearchFields);
    return this.client.get(req);
  }

  search(context
  /*: AccessContext*/
  = new AccessContext(null), userId
  /*: ?string*/
  , roleIds
  /*: ?[string]*/
  , requestFields
  /*: ?[string]*/
  , useScroll
  /*: ?boolean*/
  , limit
  /*: ?number*/
  ) {
    const {
      propertyId,
      businessId,
      locationId
    } = context && context.serialize() || {};
    const path = `/properties/${propertyId}/accesses/search`;
    const params = {
      businessId,
      locationId,
      userId,
      roleIds: strList(roleIds),
      limit
    };
    const req = new this.client.Request(path, params, requestFields || searchFields);
    req.useScroll = useScroll;
    return this.client.get(req);
  }

  list(context
  /*: AccessContext*/
  = new AccessContext(null), userId
  /*: string*/
  , transform
  /*: (PagedList<Object>) => PagedList<Object>*/
  )
  /*: (Promise<PagedList<Object>>)*/
  {
    const {
      propertyId,
      businessId,
      locationId
    } = context && context.serialize() || {};
    const url = [propertyId ? `/properties/${propertyId}` : '', businessId ? `/businesses/${businessId}` : '', locationId ? `/locations/${locationId}` : '', `/accesses`].join('');
    const req = new this.client.Request(url, {
      limit: 50,
      userId
    }, fields);
    req.transform = transform;
    req.useCursor = true;
    return this.client.get(req);
  }

  getPropertyUserAccesses(propertyId, userId) {
    const url = `/properties/${propertyId}/users/${userId}/accesses/search`;
    const req = new this.client.Request(url, {}, ['id', 'propertyId', 'businessId', 'locationId', 'roleId']);
    return this.client.get(req);
  }

  promote(context
  /*: AccessContext*/
  = new AccessContext(null), userId
  /*: string*/
  , roleId
  /*: string*/
  )
  /*: Promise<any>*/
  {
    const {
      propertyId,
      businessId,
      locationId
    } = context && context.serialize() || {};
    const path = [propertyId ? `/properties/${propertyId}` : '', businessId ? `/businesses/${businessId}` : '', locationId ? `/locations/${locationId}` : '', '/promote'].join('');
    const req = new this.client.Request(path, {
      userId,
      roleId
    }, promoteFields);
    return this.client.post(req).catch(err => {
      // User already has this Role for this context so treat as a success
      if (err.messageId === '02-67-006') return true;
      throw err;
    });
  }

  demote(context
  /*: AccessContext*/
  = new AccessContext(null), userId
  /*: string*/
  , roleId
  /*: string*/
  )
  /*: Promise<any>*/
  {
    const {
      propertyId,
      businessId,
      locationId
    } = context && context.serialize() || {};
    const path = [propertyId ? `/properties/${propertyId}` : '', businessId ? `/businesses/${businessId}` : '', locationId ? `/locations/${locationId}` : '', `/demote`].join('');
    const req = new this.client.Request(path, {
      userId,
      roleId
    }, []);
    return this.client.post(req).catch(err => {
      // User already does not have this Role for this context so treat as a success
      if (err.status === 404 && err.messageId === '02-67-003') return true;
      throw err;
    });
  } // TODO: Replace this with purpose-built backend call


  demoteAll(context
  /*: AccessContext*/
  , userId
  /*: string*/
  , recursive
  /*: boolean*/
  )
  /*: Promise<any>*/
  {
    const _this = this;

    return _call(function () {
      const {
        propertyId,
        businessId
      } = context.serialize();
      const path = [propertyId ? `/properties/${propertyId}` : '', businessId ? `/businesses/${businessId}` : '', '/demoteAll'].join('');
      const params = {
        userId,
        recursive
      };
      const req = new _this.client.Request(path, params);
      return _await(_this.client.post(req));
    });
  }

  checkPermissions(context
  /*: AccessContext | Object*/
  = {}, perms
  /*: Array<string>*/
  )
  /*: Promise<boolean>*/
  {
    const _this2 = this;

    return _call(function () {
      // $FlowFixMe -- Doesn't understand that serialize will be called if it's an AccessContext
      const {
        propertyId,
        businessId,
        locationId
      } = context.serialize ? context.serialize() : context;

      let promise = _this2.permCache.fetch(propertyId, businessId, locationId, perms);

      if (!promise) {
        const path = `/self/accesses/check`;
        const params = {
          propertyId,
          businessId,
          locationId,
          perms
        };
        const req = new _this2.client.Request(path, params);
        promise = _this2.client.post(req);

        _this2.permCache.store(propertyId, businessId, locationId, perms, promise);
      }

      return _await(promise, function ({
        granted
      }) {
        return granted;
      });
    });
  } // eslint-disable-next-line class-methods-use-this


  clearPermissionCache() {
    this.permCache.cache = {};
  }

}

class PermissionCache {
  constructor() {
    this.cache = {};
  }
  /* eslint-disable-next-line class-methods-use-this */


  makeKey(propertyId, businessId, locationId, perms) {
    return `${propertyId}${businessId}${locationId}${perms.join(',')}`;
  }

  store(propertyId, businessId, locationId, perms, promise) {
    this.cache[this.makeKey(propertyId, businessId, locationId, perms)] = promise;
  }

  fetch(propertyId, businessId, locationId, perms) {
    return this.cache[this.makeKey(propertyId, businessId, locationId, perms)];
  }

}

module.exports = AccessRepo;